import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import GroupCard from '@web/src/features/group/group-card/group-card'
import {
  type GroupListFilterFormValues,
  groupListFilterFormValuesFromResolvedParams,
} from '@web/src/features/group/group-list-filter-types'
import GroupListSideNav from '@web/src/features/group/group-list-side-nav/group-list-side-nav'
import GroupListTopNav from '@web/src/features/group/group-list-top-nav/group-list-top-nav'
import GroupsMenu from '@web/src/features/group/groups-menu/groups-menu'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { baseUrl } from '@web/src/lib/utilsClient'
import { LayoutGridIcon, LayoutListIcon, UsersIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  GroupSortEnum,
  GroupsDiscoveryTagsQuery,
  GroupsDiscoveryTagsQueryVariables,
  MyPendingGroupsPageQuery,
  MyPendingGroupsPageQueryVariables,
} from 'types/graphql'
import { z } from 'zod'

const TAGS_QUERY: TypedDocumentNode<GroupsDiscoveryTagsQuery, GroupsDiscoveryTagsQueryVariables> = gql`
  query GroupsDiscoveryTagsQuery {
    groupTags {
      id
      name
    }
  }
`

export const QUERY: TypedDocumentNode<MyPendingGroupsPageQuery, MyPendingGroupsPageQueryVariables> = gql`
  query MyPendingGroupsPageQuery($page: Int, $filter: MyPendingGroupsFilter, $sortBy: GroupSortEnum) {
    myPendingGroups(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        group {
          id
          slug
          name
          createdAt
          tagLine
          visibility
          groupType
          memberCount
          coverNsfwRating
          coverImageUrlMedium
          coverImageOffsetX
          coverImageOffsetY
          ownerId
          myMembership {
            id
            role
          }
          tags {
            id
            name
          }
        }
        type
        createdAt
        message
      }
      count
      perPage
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/group/pending')({
  component: PendingGroupsPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'NEWEST',
      }),
    ],
  },
  loaderDeps: ({ search: { page, name, sortBy } }) => ({ page, name, sortBy }),
  loader: async ({ context, deps }) => {
    const pageInt = deps.page || 1
    const name = deps.name || ''
    const sortBy = (deps.sortBy || 'NEWEST') as GroupSortEnum

    const [{ queryRef }, { queryRef: tagsQueryRef }] = await Promise.all([
      queryAndPreload(context, QUERY, {
        variables: {
          page: pageInt,
          filter: {
            name: name,
          },
          sortBy: sortBy,
        },
      }),
      queryAndPreload(context, TAGS_QUERY),
    ])
    return { queryRef, tagsQueryRef }
  },
  head: () => {
    const tr = i18n.t.bind(i18n)
    const pathname = '/group/pending'
    const jsonLdData = [
      {
        '@context': 'https://schema.org',
        '@type': 'SearchResultsPage',
        url: `${baseUrl()}${pathname}`,
        name: tr('Pending Groups'),
        description: tr(
          'Browse pending group invitations and join requests in one place so you can quickly manage what needs your attention.'
        ),
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${baseUrl()}${pathname}?name={query}`,
          },
          queryInput: 'required name=query',
        },
      },
    ]

    return seo({
      title: `${tr('Pending Groups')} | ${env.VITE_APP_NAME}`,
      description: tr(
        'Browse pending group invitations and join requests in one place so you can quickly manage what needs your attention.'
      ),
      robots: robotsMetaForEnvironment({
        production: 'index, follow',
        staging: 'noindex,nofollow',
      }),
      jsonLd: jsonLdData,
    })
  },
})

type GroupCardLayoutMode = 'compact' | 'regular'

function LayoutToggleButton({ layout, onToggle }: { layout: GroupCardLayoutMode; onToggle: () => void }) {
  const { t } = useTranslation()
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={layout === 'compact' ? t('Switch to stacked group cards') : t('Switch to compact group cards')}
            onClick={onToggle}
            type="button"
          />
        }
      >
        {layout !== 'compact' ? <LayoutGridIcon className="size-4" /> : <LayoutListIcon className="size-4" />}
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {layout === 'compact' ? t('Show image above details') : t('Show image beside details')}
      </TooltipContent>
    </Tooltip>
  )
}

function PendingGroupsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false })
  const searchRef = useRef(search)
  searchRef.current = search

  const { isAuthenticated } = useAuth()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)
  const { queryRef, tagsQueryRef } = Route.useLoaderData()
  const { data, error } = useReadQuery(queryRef as QueryRef<MyPendingGroupsPageQuery>)
  const { data: tagsData } = useReadQuery(tagsQueryRef as QueryRef<GroupsDiscoveryTagsQuery>)
  const [cardLayout, setCardLayout] = useState<GroupCardLayoutMode>('compact')

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const pageInt = search.page || 1

  const params = useMemo(
    () => ({
      name: search.name || '',
      sortBy: search.sortBy || 'NEWEST',
    }),
    [search.name, search.sortBy]
  )

  const listFilterDefaults = useMemo(
    (): GroupListFilterFormValues =>
      groupListFilterFormValuesFromResolvedParams({
        name: params.name,
        tags: [],
        excludeTags: [],
        sortBy: params.sortBy,
      }),
    [params.name, params.sortBy]
  )

  const listFilterResetValues = useMemo(
    (): GroupListFilterFormValues =>
      groupListFilterFormValuesFromResolvedParams({
        name: undefined,
        tags: [],
        excludeTags: [],
        sortBy: undefined,
      }),
    []
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: params.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: params.sortBy })
  }, [params.sortBy, sortFormMethods])

  const applyFilters = useCallback(
    (values: GroupListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      const nextSort = values.sortBy || undefined
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          name: values.name || undefined,
          ...(options.includeSort ? { sortBy: nextSort } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handlePageChange = useCallback(
    (nextPage: number) => {
      navigate({
        search: { ...searchRef.current, page: nextPage },
        replace: true,
      })
    },
    [navigate]
  )

  const handleReset = useCallback(() => {
    navigate({ to: '/group/pending', search: {}, replace: true } as never)
  }, [navigate])

  const groupsPageData = useMemo(
    () => data?.myPendingGroups || { entries: [], count: 0, perPage: 24 },
    [data?.myPendingGroups]
  )
  const resolvedGroupTags = tagsData?.groupTags ?? []

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="flex w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <GroupListSideNav
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          isAuthenticated={isAuthenticated}
          onFiltersApply={(values) => applyFilters(values, { includeSort: false })}
          onReset={handleReset}
          showTagModeToggle={false}
          tags={resolvedGroupTags}
        />
      </aside>

      <div className="flex w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="w-full max-w-full px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: t('Pending Groups') }]}
            className="mb-0 items-stretch md:mb-4"
            highlightTitle={t('Groups')}
            preTitle={t('Pending')}
            rightChild={
              <div className="hidden h-full flex-col items-end justify-end gap-3 md:flex">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 text-nowrap font-medium text-muted-foreground text-sm">
                    {t('Sort By:')}
                  </span>
                  <Form formMethods={sortFormMethods} suppressHydrationWarning>
                    <NativeSelectFormField name="sortBy" onValueChange={handleSortChange}>
                      <option value="NEWEST">{t('Newest')}</option>
                      <option value="OLDEST">{t('Oldest')}</option>
                      <option value="NAME">{t('Name')}</option>
                    </NativeSelectFormField>
                  </Form>
                  <LayoutToggleButton
                    layout={cardLayout}
                    onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                  />
                </div>
              </div>
            }
            subtitle={t(
              'Browse pending group invitations and join requests in one place so you can quickly manage what needs your attention.'
            )}
          />

          <div className="mb-6 w-full md:hidden">
            <div className="mt-4">
              <GroupListTopNav
                filterDefaults={listFilterDefaults}
                filterResetValues={listFilterResetValues}
                isAuthenticated={isAuthenticated}
                onFiltersApply={(values) => applyFilters(values, { includeSort: true })}
                onReset={handleReset}
                rightBarChildren={
                  <LayoutToggleButton
                    layout={cardLayout}
                    onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                  />
                }
                showTagModeToggle={false}
                tags={resolvedGroupTags}
              />
            </div>
          </div>

          {isAuthenticated && (
            <div className="mb-8">
              <GroupsMenu activeTab="pending" listRoute="/group/list" />
            </div>
          )}

          {groupsPageData.entries.length > 0 ? (
            <div className="flex w-full flex-col gap-0">
              <div
                className={
                  cardLayout === 'compact'
                    ? 'grid w-full grid-cols-1 items-stretch gap-4 md:gap-6'
                    : 'grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4'
                }
              >
                {groupsPageData.entries.map((pendingGroup) => (
                  <div className="relative flex flex-col" key={pendingGroup.group.id}>
                    <GroupCard cardLayout={cardLayout} group={pendingGroup.group} />
                    <Badge
                      className={
                        pendingGroup.type === 'INVITATION'
                          ? 'absolute -top-3 left-0 bg-primary text-primary-foreground shadow-lg'
                          : 'absolute -top-3 left-0 bg-amber-500 text-black shadow-lg'
                      }
                      size="sm"
                      variant="default"
                    >
                      {pendingGroup.type === 'INVITATION' ? t('Invited To Join') : t('Membership Request In Review')}
                    </Badge>
                  </div>
                ))}
              </div>

              <PaginationControl
                count={groupsPageData.count ? groupsPageData.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={pageInt}
                pageSize={groupsPageData.perPage ? groupsPageData.perPage : 1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <UsersIcon className="size-8" />
                </EmptyMedia>
                <EmptyTitle>{t('No groups found')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>

        <Footer />
      </div>
    </div>
  )
}
